import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import type { components } from '@bookingdei/api-client/types';
import { useLanguage } from '../../shell/language-context';
import { useOfferPopup } from '../offers/offer-popup';
import { dealView, offerView, type OfferView } from '../offers/offer-view';
import { useRailViews } from './offer-rail-card';
import { Cover } from './hx-cover';
import { Ic } from './hx-icon';
import type { HomeFormat } from './use-home-format';

type OfferCardData = components['schemas']['OfferCard'];
type DealData = components['schemas']['DirectoryOfferCard'];
/** One card: a bookable offer, or a deal a business announced itself (no booking; FR-A26 v3). */
type Item = { offer: OfferCardData; deal?: never } | { deal: DealData; offer?: never };
type ChipId = 'all' | 'food' | 'beauty' | 'stays' | 'ending' | 'bookable';

const categoryOf = (o: Item) => (o.offer ? (o.offer.category_id ?? o.offer.business.category_id) : o.deal.category_id);
// The design's six chips: three narrow by category (registry ids), two by facts the server
// already computed (ends_in_days, bookable).
const CHIPS: { id: ChipId; label: 'dc50.offers.chip_all' | 'dc50.offers.chip_food' | 'dc50.offers.chip_beauty' | 'dc50.offers.chip_stays' | 'dc50.offers.chip_ending' | 'dc50.offers.chip_bookable'; match: (o: Item) => boolean }[] = [
  { id: 'all', label: 'dc50.offers.chip_all', match: () => true },
  { id: 'food', label: 'dc50.offers.chip_food', match: (o) => categoryOf(o) === 'restaurant' },
  { id: 'beauty', label: 'dc50.offers.chip_beauty', match: (o) => categoryOf(o) === 'salon' },
  { id: 'stays', label: 'dc50.offers.chip_stays', match: (o) => categoryOf(o) === 'hotel' },
  { id: 'ending', label: 'dc50.offers.chip_ending', match: (o) => ((o.offer ?? o.deal).ends_in_days ?? 99) <= 2 },
  { id: 'bookable', label: 'dc50.offers.chip_bookable', match: (o) => Boolean(o.offer?.bookable) },
];

function OfferTile({ view, categoryId, onOpen }: { view: OfferView; categoryId: string | undefined; onOpen: (v: OfferView) => void }) {
  const { t } = useLanguage();
  return (
    <button type="button" className="oc rv" onClick={() => onOpen(view)} aria-label={t('dc52.popup.open', { offer: `${view.bizName} — ${view.title}` })}>
      <Cover src={view.image?.poster} categoryId={categoryId} id={view.key} />
      <div className="ot">
        <div className="row">
          <span className="tag">{view.off}</span>
          {view.sponsored ? <span className="bdg">{t('dc52.sponsored')}</span> : null}
          <span className="left">
            <Ic id="clock" />
            {view.ends.label}
          </span>
        </div>
        <b>{view.bizName}</b>
        <p>{view.title}</p>
        <small>{[view.category, view.area].filter(Boolean).join(' · ')}</small>
      </div>
    </button>
  );
}

// "Today's offers." (design `.sec.alt`, FR-C19): the context line, six filter chips, a rail
// of offer cards that drifts on its own, and the deal-alerts line. Chips filter the rail the
// home aggregate already carries; /offer is where server-side filtering lives. Businesses'
// own announced deals follow the bookable offers. A card opens the offer popup, rendered on
// <body> so the design's element resets (.hx button, .hx a) never restyle it.
export function HxOffers({ offers, deals, fmt }: { offers: OfferCardData[]; deals: DealData[]; fmt: HomeFormat }) {
  const { t } = useLanguage();
  const [chip, setChip] = useState<ChipId>('all');
  const ctx = useRailViews(fmt);
  const { open, popup } = useOfferPopup();
  const items: Item[] = [...offers.map((offer) => ({ offer })), ...deals.map((deal) => ({ deal }))];
  const list = items.filter(CHIPS.find((c) => c.id === chip)!.match);

  return (
    <section className="sec alt">
      <div className="in">
        <div className="sh rv">
          <div>
            <h2>{t('dc50.offers.title')}</h2>
            <p>{t('dc50.offers.context')}</p>
          </div>
          <Link className="more" to="/offer">
            {t('hx.offers.see_all')} <Ic id="arrow" />
          </Link>
        </div>
        <div className="chips rv" role="toolbar" aria-label={t('dc50.offers.filter_label')}>
          {CHIPS.map((c) => (
            <button key={c.id} type="button" className={chip === c.id ? 'chip on' : 'chip'} aria-pressed={chip === c.id} onClick={() => setChip(c.id)}>
              {t(c.label)}
            </button>
          ))}
        </div>
        {list.length > 0 ? (
          <div className="hs" data-auto>
            {list.map((o) => {
              const view = o.offer ? offerView(o.offer, ctx) : dealView(o.deal, ctx);
              return <OfferTile key={view.key} view={view} categoryId={categoryOf(o)} onOpen={open} />;
            })}
          </div>
        ) : (
          <p className="foot">{t('dc50.offers.empty')}</p>
        )}
        <p className="foot rv">{t('dc50.offers.alerts')}</p>
      </div>
      {popup ? createPortal(popup, document.body) : null}
    </section>
  );
}
